import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { unwrapFetch2 } from '~/fetch';
import type {
	ReportRequestRow,
	RequestOffOrderGuideSavingsReportArgs,
} from '~/ogm-hub';
import { ReportController } from '~/ogm-hub';
import type { IAppState } from '~/appstate';

export interface IReportDateRangeModalState {
	loadingStates: {
		submittingRequest: boolean;
		submitSuccess: boolean;
	};
	selections: {
		startDate: string;
		endDate: string;
	};
	errors: {
		requestReportError: boolean;
	};
}

const initialState: IReportDateRangeModalState = {
	loadingStates: {
		submittingRequest: false,
		submitSuccess: false,
	},
	selections: {
		startDate: new Date().toISOString().split('T')[0],
		endDate: new Date().toISOString().split('T')[0],
	},
	errors: {
		requestReportError: false,
	},
};

export const submitOffOrderGuideSavingsRequest = createAsyncThunk<
	ReportRequestRow,
	null,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportDateRangeSlice/submitOffOrderGuideSavingsRequest',
	async (_, { getState, rejectWithValue }) => {
		const state = getState();
		const {
			selections: { startDate, endDate },
		} = state.reportsPage.reportDateRangeModalState;

		const args: RequestOffOrderGuideSavingsReportArgs = {
			startDate,
			endDate,
		};

		const response = await unwrapFetch2(
			ReportController.RequestOffOrderGuideSavingsReport(args)
		);

		if (response.errors != null && response.errors.length > 0) {
			rejectWithValue(response.errors);
		}

		return response.reportRequestRow;
	}
);

export const reportDateRangeModalSlice = createSlice({
	name: 'reportDateRangeSlice',
	initialState,
	reducers: {
		resetModal: (state) => {
			state.loadingStates = initialState.loadingStates;
			state.selections = initialState.selections;
		},
		updateStartDate: (state, action) => {
			state.selections.startDate = action.payload;
		},
		updateEndDate: (state, action) => {
			state.selections.endDate = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(submitOffOrderGuideSavingsRequest.pending, (state) => {
			state.loadingStates.submittingRequest = true;
		});
		builder.addCase(submitOffOrderGuideSavingsRequest.fulfilled, (state) => {
			state.loadingStates = initialState.loadingStates;
			state.selections = initialState.selections;
		});
		builder.addCase(submitOffOrderGuideSavingsRequest.rejected, (state) => {
			state.errors.requestReportError = true;
			state.loadingStates.submittingRequest = false;
		});
	},
});

export const { resetModal, updateStartDate, updateEndDate } =
	reportDateRangeModalSlice.actions;
